import { cn } from '@/lib/utils'
import { Modal } from '@/components/Modal'
import { InfoPopover } from '@/components/InfoPopover'
import { MONO, eurS } from './format'

export interface DrillLine {
  name: string
  prev?: number
  curr: number
}

export interface DrillContent {
  tag: string
  title: string
  sub?: string
  total?: number
  why?: string
  /** Glossary term shown beside the total (e.g. `category:<id>`). */
  term?: string
  lines: DrillLine[]
}

interface DrillModalProps {
  content: DrillContent | null
  onClose: () => void
}

/** Drill-down modal for breakdown segments and movers — shows the underlying
 *  lines, with prior vs current when a comparison exists. */
export function DrillModal({ content, onClose }: DrillModalProps) {
  const hasPrev = content?.lines.some((l) => l.prev != null) ?? false

  return (
    <Modal
      open={!!content}
      onClose={onClose}
      eyebrow={content?.tag}
      title={content?.title ?? ''}
      subtitle={content?.sub}
      footer="Illustrative line items — the live build opens the underlying AR transactions for this selection."
    >
      {content && (
        <>
          {content.total != null && (
            <p className={cn('mb-4 flex items-center gap-2 text-2xl font-semibold tracking-tight', MONO)}>
              {eurS(content.total)}
              {content.term && (
                // Radix moves focus into the dialog on open, which would pop the glossary
                // tip over the lines for a mouse user. Only keyboard focus reveals it here;
                // hover and tap work as everywhere else.
                <span
                  className="inline-flex"
                  onFocusCapture={(e) => {
                    if (!(e.target as HTMLElement).matches(':focus-visible')) e.stopPropagation()
                  }}
                >
                  <InfoPopover term={content.term} />
                </span>
              )}
            </p>
          )}
          {content.why && (
            <p className="mb-5 rounded-lg bg-champagne-50/60 px-3.5 py-2.5 text-xs leading-relaxed text-stone-600 dark:bg-champagne-500/10 dark:text-stone-300">
              {content.why}
            </p>
          )}

          {/* Mobile: stacked cards — the comparison columns collide at narrow widths */}
          <div className="space-y-2.5 sm:hidden">
            {content.lines.map((l, i) => {
              const d = l.prev != null ? l.curr - l.prev : null
              const up = d != null && d >= 0
              return (
                <div key={i} className="rounded-lg border border-stone-100 bg-stone-50/50 p-3 dark:border-stone-800 dark:bg-stone-800/30">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-sm font-medium text-stone-700 dark:text-stone-200">{l.name}</span>
                    {hasPrev ? (
                      <span className={cn('shrink-0 text-sm font-semibold', MONO, d == null ? 'text-stone-400' : up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                        {d == null ? '—' : eurS(d)}
                      </span>
                    ) : (
                      <span className={cn('shrink-0 text-sm font-semibold', MONO)}>{eurS(l.curr)}</span>
                    )}
                  </div>
                  {hasPrev && (
                    <div className="mt-2.5 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-xs">
                      <span className="text-stone-400">Prior</span>
                      <span className={cn('text-right text-stone-500 dark:text-stone-400', MONO)}>{l.prev != null ? eurS(l.prev) : '—'}</span>
                      <span className="text-stone-400">Current</span>
                      <span className={cn('text-right text-stone-700 dark:text-stone-200', MONO)}>{eurS(l.curr)}</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Desktop: full comparison table */}
          <table className="hidden w-full text-sm sm:table">
            <thead>
              <tr className="border-b border-stone-200 text-xs uppercase tracking-wide text-stone-400 dark:border-stone-700 dark:text-stone-500">
                <th className="pb-3 pr-4 text-left font-medium">Line</th>
                {hasPrev && <th className="pb-3 pl-4 text-right font-medium">Prior</th>}
                <th className="pb-3 pl-4 text-right font-medium">Current</th>
                {hasPrev && <th className="pb-3 pl-6 text-right font-medium">Δ</th>}
              </tr>
            </thead>
            <tbody>
              {content.lines.map((l, i) => {
                const d = l.prev != null ? l.curr - l.prev : null
                return (
                  <tr key={i} className="even:bg-stone-50/50 dark:even:bg-stone-800/20">
                    <td className="py-3.5 pr-4 text-stone-700 dark:text-stone-200">{l.name}</td>
                    {hasPrev && <td className={cn('whitespace-nowrap py-3.5 pl-4 text-right text-stone-400', MONO)}>{l.prev != null ? eurS(l.prev) : '—'}</td>}
                    <td className={cn('whitespace-nowrap py-3.5 pl-4 text-right', MONO)}>{eurS(l.curr)}</td>
                    {hasPrev && (
                      <td className={cn('whitespace-nowrap py-3.5 pl-6 text-right', MONO, d == null ? '' : d >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                        {d == null ? '—' : eurS(d)}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      )}
    </Modal>
  )
}
